import * as React from "react";
import moment from "moment-timezone";

import type { Job } from "./ApplicationInvitations";

interface Invite {
  id: number;
  ignored: boolean;
  seen?: boolean;
  applied?: boolean;
  created_at: string;
  job: Job;
}

interface InviteProps {
  invite: Invite;
  authenticity_token: string;
}

export const InviteComponent: React.FC<InviteProps> = ({ invite, authenticity_token }) => {
  const [ignored, setIgnored] = React.useState(invite.ignored);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const { job } = invite;

  // Format invitation date
  const invitationDate = React.useMemo(() => {
    if (!invite.created_at || !job.practice.timezone) {
      return 'Date unavailable';
    }
    return moment.tz(invite.created_at, job.practice.timezone).format("ddd, MMM D, YYYY h:mm A");
  }, [invite.created_at, job.practice.timezone]);

  const handleIgnore = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`/application_invites/${invite.id}.json`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": authenticity_token,
        },
        body: JSON.stringify({ ignore: !ignored }),
      });

      if (response.ok) {
        setIgnored(!ignored);
      } else {
        throw new Error('Failed to update invite status');
      }
    } catch (error) {
      console.error("Error updating invite:", error);
      setError('Failed to update invite. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on buttons or links
    if ((e.target as HTMLElement).closest('button, a')) {
      return;
    }
    window.location.href = `/jobs/${job.id}`;
  };

  return (
    <div
      className="field"
      style={{ cursor: 'pointer' }}
      onClick={handleClick}
    >
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          <i className="fa fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      <li className="col-12 d-flex justify-content-between border-top border-start border-end py-2 pt-1 pb-3 border-radius-1">
        <div className="col-2 p-3" style={{ minHeight: 40 }}>
          <img
            src={job.practice.avatar_url}
            alt={`${job.practice_name} logo`}
            className="img-fluid rounded-circle"
          />
        </div>

        <div className="col-10 d-flex flex-column justify-content-center">
          <div className="job-list-content">
            <div className="d-flex flex-row align-items-center pe-2">
              <h4 className="mb-0 mt-2 col-6 col-xl-8">
                {job.job_type.title}
              </h4>
              <div className="col-6 col-xl-4 text-end">
                <span className="badge bg-primary">
                  Invitation
                </span>
              </div>
            </div>
            <div className="d-flex my-2 flex-wrap">
              <span className="profile-attribute-bubble rounded px-2 py-1 me-1">
                <i className="fa fa-briefcase"></i> {job.practice_name}
              </span>
              {job.practice.city && job.practice.state && (
                <span className="profile-attribute-bubble rounded px-2 py-1 me-1">
                  <i className="fa fa-map-marker"></i> {job.practice.city}, {job.practice.state}
                </span>
              )}
              {job.compensation_full && (
                <span className="profile-attribute-bubble rounded px-2 py-1 me-1">
                  <i className="fa fa-money"></i> {job.compensation_full}
                </span>
              )}
              <span className="profile-attribute-bubble rounded px-2 py-1 me-1">
                <i className="fa fa-envelope"></i> Invited {invitationDate}
              </span>
              {invite.seen && (
                <span className="profile-attribute-bubble rounded px-2 py-1 me-1 text-info">
                  <i className="fa fa-eye"></i> Viewed
                </span>
              )}
              {invite.applied && (
                <span className="profile-attribute-bubble rounded px-2 py-1 me-1 text-success">
                  <i className="fa fa-check"></i> Applied
                </span>
              )}
            </div>

            {/* Action buttons row */}
            <div className="d-flex gap-2 mt-2">
              {!invite.applied && !ignored && (
                <>
                  <a
                    href={`/jobs/${job.id}/apply`}
                    className="btn btn-primary shadow"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="fa fa-paper-plane me-1"></i>Apply Now
                  </a>

                  <button
                    className="btn btn-light shadow"
                    onClick={handleIgnore}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Ignoring...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-times me-1"></i>
                        Ignore
                      </>
                    )}
                  </button>
                </>
              )}

              {invite.applied && (
                <span className="badge bg-success fs-6">
                  <i className="fa fa-check me-1"></i>
                  Applied
                </span>
              )}

              {ignored && (
                <div className="d-flex gap-2">
                  <span className="badge bg-secondary fs-6">
                    <i className="fa fa-eye-slash me-1"></i>
                    Ignored
                  </span>
                  <button
                    className="btn btn-outline-primary shadow btn-sm"
                    onClick={handleIgnore}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Unignoring...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-undo me-1"></i>
                        Unignore
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </li>
      <div className="clearfix"></div>
    </div>
  );
};
